import { apiClient } from './api';
import type { ChatRequest, Viseme } from './api';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  visemes?: Viseme[];
  audioUrl?: string;
  booking_ready?: boolean;
  booking_data?: {
    boat_model: string;
    boat_length: number;
    boat_width: number;
    boat_draft: number;
    check_in_date: string;
    check_out_date: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    mooring_name?: string;
    notes?: string;
  };
}

export type { Message as MessageType };

export class ChatService {
  private conversationId: string;
  private messageHistory: Message[] = [];

  constructor() {
    this.conversationId = `conv-${Date.now()}`;
  }

  /**
   * Send message and get AI response
   */
  async sendMessage(message: string, language?: string): Promise<Message> {
    // Add user message to history
    const userMessage: Message = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: Date.now(),
    };
    this.messageHistory.push(userMessage);

    try {
      // Call API
      const response = await apiClient.chat({
        message,
        conversation_id: this.conversationId,
        language: language || 'it',
      } as ChatRequest);

      // Add assistant message to history
      const assistantMessage: Message = {
        id: `msg-${Date.now()}-1`,
        role: 'assistant',
        content: response.response,
        timestamp: Date.now(),
        visemes: response.visemes,
        booking_ready: response.booking_ready,
        booking_data: response.booking_data,
      };
      this.messageHistory.push(assistantMessage);

      return assistantMessage;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Get message history
   */
  getHistory(): Message[] {
    return this.messageHistory;
  }

  /**
   * Clear conversation
   */
  clearHistory(): void {
    this.messageHistory = [];
    this.conversationId = `conv-${Date.now()}`;
  }

  /**
   * Get current conversation ID
   */
  getConversationId(): string {
    return this.conversationId;
  }

  /**
   * Synthesize assistant message to speech
   */
  async synthesizeMessage(message: Message): Promise<{ audioUrl?: string; visemes: Viseme[] }> {
    try {
      const response = await apiClient.synthesize({
        text: message.content,
      });

      return {
        audioUrl: response.audio_url || response.audio_base64,
        visemes: response.visemes || [],
      };
    } catch (error) {
      console.error('Error synthesizing message:', error);
      throw error;
    }
  }

  /**
   * Transcribe audio to text
   */
  async transcribeAudio(audioBase64: string, language: string = 'it'): Promise<string> {
    try {
      const response = await apiClient.transcribe({
        audio_base64: audioBase64,
        language,
      });
      return response.text;
    } catch (error) {
      console.error('Error transcribing audio:', error);
      throw error;
    }
  }
}

export const chatService = new ChatService();
